import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { AttemptState, SubmitResult } from 'shared';
import { ExamTimerDisplay } from '@/components/exam/ExamTimer';
import { QuestionCard } from '@/components/exam/QuestionCard';
import { QuestionNavigator } from '@/components/exam/QuestionNavigator';
import { SaveIndicator } from '@/components/exam/SaveIndicator';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { useAutoSave } from '@/hooks/useAutoSave';
import { useExamTimer } from '@/hooks/useExamTimer';
import { apiFetch } from '@/lib/api';
import { translateApiError } from '@/lib/api-error';
import { attemptErrorOutcome } from '@/lib/attempt-outcome';
import { chunk, firstIndexOfPage, pageOfIndex } from '@/lib/paginate';
import {
  clearDraft,
  clearSessionToken,
  draftFromSavedAnswers,
  isAnswered,
  readDraft,
  readSessionToken,
  type AnswerDraft,
} from '@/lib/exam-session';

const FIVE_MINUTES = 300;
const ONE_MINUTE = 60;

export function ExamPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token = '' } = useParams();
  const { pathname } = useLocation();
  const [sessionToken] = useState(() => readSessionToken());

  /**
   * หน้านี้ใช้ร่วมกันสองทางเข้า: QR/รหัสสั้น (/t/:token) กับลิงก์ส่วนตัว (/i/:token)
   * เส้นทางที่เด้งกลับจึงต้องตรงกับทางที่ผู้เรียนเข้ามา ไม่งั้นพากลับไปหน้าที่ไม่ใช่ของเขา
   */
  const fromInvite = pathname.startsWith('/i/');
  const base = `${fromInvite ? '/i' : '/t'}/${token}`;
  /** ไม่มี session แล้วต้องกลับไปที่ไหน — ลิงก์ส่วนตัวกลับหน้าตัวเอง ไม่ต้องกรอกอะไรใหม่ */
  const resumePath = fromInvite ? base : `${base}/register`;

  const [answers, setAnswers] = useState<Record<string, AnswerDraft>>({});
  /** ชุดคำตอบที่ server ยืนยันแล้วตอนเข้าหน้า (ไม่รวม draft ที่ค้างอยู่ในเครื่อง) */
  const [serverAnswers, setServerAnswers] = useState<Record<string, AnswerDraft>>({});
  const [initialized, setInitialized] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [warning, setWarning] = useState<'five' | 'one' | null>(null);
  const warned = useRef({ five: false, one: false });
  const finishedRef = useRef(false);
  /** ส่งได้ครั้งเดียวจริงๆ — กันกรณีหมดเวลาพอดีตอนกดยืนยัน แล้วยิง submit ซ้อนกันสองทาง */
  const submittingRef = useRef(false);

  const attempt = useQuery({
    queryKey: ['attempt'],
    queryFn: () =>
      apiFetch<AttemptState>('/public/attempt/me', { sessionToken: sessionToken ?? '' }),
    enabled: Boolean(sessionToken),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const state = attempt.data;

  const submit = useMutation({
    mutationFn: (auto: boolean) =>
      apiFetch<SubmitResult>('/public/attempt/submit', {
        method: 'POST',
        sessionToken: sessionToken ?? '',
        body: {
          answers: Object.entries(answers).map(([questionId, draft]) => ({
            questionId,
            selectedOptionIds: draft.selectedOptionIds,
            textAnswer: draft.textAnswer,
            isFlagged: draft.isFlagged,
          })),
        },
      }).then((result) => ({ result, auto })),
    onSuccess: ({ result, auto }) => {
      finishedRef.current = true;
      if (state) clearDraft(state.attemptId);
      clearSessionToken();
      // auto = หมดเวลาแล้วระบบส่งให้เอง — ต้องบอกผู้เรียนให้รู้ ไม่ใช่ขึ้นว่าส่งสำเร็จเฉยๆ
      void navigate(`${base}/result`, { replace: true, state: { result, expired: auto } });
    },
    onError: (error) => {
      const outcome = attemptErrorOutcome(error);
      if (outcome.kind !== 'finished') {
        // เน็ตหลุด / ยิงถี่เกินไป — ยังกดส่งใหม่ได้ ปลดล็อกปุ่มให้ผู้เรียนลองอีกครั้ง
        submittingRef.current = false;
        return;
      }

      // attempt ถูกปิดที่ server แล้ว (คำตอบอยู่ครบ) — พาไปหน้าผลลัพธ์ด้วยข้อความที่ตรงกับสาเหตุจริง
      finishedRef.current = true;
      if (state) clearDraft(state.attemptId);
      clearSessionToken();
      void navigate(`${base}/result`, {
        replace: true,
        state: { expired: outcome.expired, alreadySubmitted: outcome.alreadySubmitted },
      });
    },
  });

  /** ทางเข้าเดียวของการส่งคำตอบ — ทั้งกดเองและหมดเวลาส่งอัตโนมัติ */
  const startSubmit = useCallback(
    (auto: boolean): void => {
      if (submittingRef.current || finishedRef.current) return;
      submittingRef.current = true;
      setConfirmOpen(false);
      submit.mutate(auto);
    },
    [submit],
  );

  const handleExpire = useCallback(() => {
    // หมดเวลา = ระบบส่งให้อัตโนมัติ ไม่ใช่ error
    startSubmit(true);
  }, [startSubmit]);

  const timer = useExamTimer(
    state?.deadlineAt ?? null,
    state?.serverTime ?? new Date().toISOString(),
    handleExpire,
  );

  const autoSave = useAutoSave({
    attemptId: state?.attemptId ?? '',
    sessionToken: sessionToken ?? '',
    answers,
    // สิ่งที่ server มีอยู่แล้ว — auto-save จะได้ไม่ยิงซ้ำตอนเพิ่งเปิดหน้า
    syncedAnswers: serverAnswers,
    enabled: Boolean(state) && initialized && !finishedRef.current,
    onSaved: (result) => timer.syncWithServer(result.serverTime),
    onExpired: handleExpire,
  });

  // ไม่มี session token → กลับไปกรอกอีเมลเดิมเพื่อ resume
  useEffect(() => {
    if (!sessionToken) void navigate(resumePath, { replace: true });
  }, [sessionToken, navigate, resumePath]);

  useEffect(() => {
    if (!attempt.isError) return;
    const outcome = attemptErrorOutcome(attempt.error);
    if (outcome.kind === 'reregister') {
      clearSessionToken();
      void navigate(resumePath, { replace: true });
    } else if (outcome.kind === 'finished') {
      clearSessionToken();
      void navigate(`${base}/result`, {
        replace: true,
        state: { expired: outcome.expired, alreadySubmitted: outcome.alreadySubmitted },
      });
    }
  }, [attempt.isError, attempt.error, navigate, base, resumePath]);

  // คำตอบตั้งต้น: ของที่ server มี แล้วทับด้วย draft ในเครื่อง (draft ใหม่กว่าเสมอ)
  useEffect(() => {
    if (!state || initialized) return;
    const fromServer = draftFromSavedAnswers(state.savedAnswers);
    const local = readDraft(state.attemptId);
    setServerAnswers(fromServer);
    setAnswers(local ? { ...fromServer, ...local.answers } : fromServer);
    setInitialized(true);
  }, [state, initialized]);

  // เตือนที่ 5 นาที และ 1 นาที อย่างละครั้ง
  useEffect(() => {
    const remaining = timer.remainingSeconds;
    if (remaining === null || finishedRef.current) return;
    if (remaining <= ONE_MINUTE && !warned.current.one) {
      warned.current.one = true;
      warned.current.five = true;
      setWarning('one');
    } else if (remaining <= FIVE_MINUTES && !warned.current.five) {
      warned.current.five = true;
      setWarning('five');
    }
  }, [timer.remainingSeconds]);

  // กันปิดหน้าโดยไม่ตั้งใจระหว่างทำข้อสอบ
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent): void => {
      if (!finishedRef.current) event.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const questions = useMemo(() => state?.questions ?? [], [state]);
  const perPage = state?.questionsPerPage ?? 1;
  const pages = useMemo(() => chunk(questions, perPage), [questions, perPage]);
  const pageQuestions = pages[currentPage] ?? [];
  const firstIndex = firstIndexOfPage(currentPage, perPage);
  const unansweredCount = useMemo(
    () => questions.filter((question) => !isAnswered(answers[question.id])).length,
    [questions, answers],
  );

  const goToPage = (page: number): void => {
    setCurrentPage(Math.min(Math.max(page, 0), Math.max(pages.length - 1, 0)));
    void autoSave.flush();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ผังข้อสอบส่งลำดับ "ข้อ" มา ต้องแปลงเป็นหน้าที่ข้อนั้นอยู่
  const goToQuestion = (index: number): void => goToPage(pageOfIndex(index, perPage));

  const openConfirm = (): void => {
    void autoSave.flush();
    setConfirmOpen(true);
  };

  if (attempt.isPending || !state) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-xl items-center justify-center px-5">
        <p className="text-slate-600">
          {attempt.isError ? translateApiError(attempt.error, t) : t('common.loading')}
        </p>
      </main>
    );
  }

  return (
    <div className="min-h-dvh bg-slate-50">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-5 py-3">
          <SaveIndicator status={autoSave.status} />
          <ExamTimerDisplay remainingSeconds={timer.remainingSeconds} />
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl px-5 py-5">
        <div className="flex flex-col gap-4">
          {pageQuestions.map((question, indexOnPage) => (
            <QuestionCard
              key={question.id}
              question={question}
              index={firstIndex + indexOnPage}
              total={questions.length}
              draft={answers[question.id]}
              onChange={(next) => setAnswers((current) => ({ ...current, [question.id]: next }))}
            />
          ))}
        </div>

        <div className="mt-4 flex gap-2">
          <Button
            variant="secondary"
            className="flex-1"
            disabled={currentPage === 0}
            onClick={() => goToPage(currentPage - 1)}
          >
            <ChevronLeft aria-hidden className="h-4 w-4" />
            {t('exam.previous')}
          </Button>
          <Button
            variant="secondary"
            className="flex-1"
            disabled={currentPage >= pages.length - 1}
            onClick={() => goToPage(currentPage + 1)}
          >
            {t('exam.next')}
            <ChevronRight aria-hidden className="h-4 w-4" />
          </Button>
        </div>

        <QuestionNavigator
          questions={questions}
          answers={answers}
          currentIndex={firstIndex}
          questionsPerPage={perPage}
          onJump={goToQuestion}
        />

        <Button
          size="lg"
          className="mt-5 w-full"
          onClick={openConfirm}
          disabled={submit.isPending || finishedRef.current}
        >
          {submit.isPending ? t('exam.submitting') : t('exam.submit')}
        </Button>

        {submit.isError && !finishedRef.current && (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
            {translateApiError(submit.error, t)}
          </p>
        )}
      </main>

      <Modal
        open={confirmOpen}
        title={t('exam.confirmTitle')}
        onClose={() => setConfirmOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              {t('exam.confirmBack')}
            </Button>
            <Button disabled={submit.isPending} onClick={() => startSubmit(false)}>
              {t('exam.confirmSubmit')}
            </Button>
          </>
        }
      >
        <p>
          {unansweredCount > 0
            ? t('exam.confirmUnanswered', { count: unansweredCount })
            : t('exam.confirmAllAnswered')}
        </p>
      </Modal>

      <Modal
        open={warning !== null}
        title={t('exam.timeLeft')}
        onClose={() => setWarning(null)}
        footer={<Button onClick={() => setWarning(null)}>{t('common.close')}</Button>}
      >
        <p>{warning === 'one' ? t('exam.warning1min') : t('exam.warning5min')}</p>
      </Modal>
    </div>
  );
}
